import React, { createContext, useContext, useEffect, useMemo, useRef, useState } from "react";
import { FREE_DELIVERY_THRESHOLD, DELIVERY_FEE } from "../data/products";
import { useAuth } from "./AuthContext";
import api from "../lib/api";

const CartContext = createContext(null);
const STORAGE_KEY = "sandform_cart";

export const CartProvider = ({ children }) => {
  const { user } = useAuth();
  const [items, setItems] = useState(() => {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      return raw ? JSON.parse(raw) : [];
    } catch {
      return [];
    }
  });
  const [open, setOpen] = useState(false);
  const saveTimer = useRef(null);

  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(items));
  }, [items]);

  // Sync cart to backend (debounced) for abandoned-cart nudges — only when logged in.
  useEffect(() => {
    if (!user) return;
    if (saveTimer.current) clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(() => {
      api.post("/cart/save", { items: items.map((i) => ({ product_id: i.product_id, quantity: i.quantity })) }).catch(() => {});
    }, 1500);
    return () => saveTimer.current && clearTimeout(saveTimer.current);
  }, [items, user]);

  const addItem = (product, vial) => {
    setItems((prev) => {
      const found = prev.find((i) => i.product_id === product.id);
      if (found) {
        return prev.map((i) => (i.product_id === product.id ? { ...i, quantity: i.quantity + 1 } : i));
      }
      return [...prev, { product_id: product.id, name: product.name, price: product.price, vial, quantity: 1 }];
    });
  };

  const removeItem = (product_id) => setItems((prev) => prev.filter((i) => i.product_id !== product_id));

  const updateQty = (product_id, quantity) => {
    if (quantity < 1) return removeItem(product_id);
    setItems((prev) => prev.map((i) => (i.product_id === product_id ? { ...i, quantity: Math.min(quantity, 20) } : i)));
  };

  const clear = () => setItems([]);

  const { count, subtotal, deliveryFee, total } = useMemo(() => {
    const count = items.reduce((s, i) => s + i.quantity, 0);
    const subtotal = items.reduce((s, i) => s + i.price * i.quantity, 0);
    const deliveryFee = subtotal === 0 ? 0 : subtotal >= FREE_DELIVERY_THRESHOLD ? 0 : DELIVERY_FEE;
    return { count, subtotal, deliveryFee, total: subtotal + deliveryFee };
  }, [items]);

  return (
    <CartContext.Provider
      value={{ items, open, setOpen, addItem, removeItem, updateQty, clear, count, subtotal, deliveryFee, total }}
    >
      {children}
    </CartContext.Provider>
  );
};

export const useCart = () => useContext(CartContext);
